import { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { formatMoney, setDealDemo, useDeals, type Deal } from "../services/dealsService";
import { fetchHubspotPipelines, type HubspotPipeline } from "../services/hubspotService";
import { demoBadgeOf } from "../services/dealDemoService";
import { useIsReseller } from "../../../hooks/useIsReseller";
import Modal from "../../../components/base/Modal";
import DealDetail from "./DealDetail";
import { boardScrollbar } from "./boardScroll";

// ===========================================================================
// Tablero de negocios — the SAME columns as the HubSpot deal pipeline.
//
// The lead board (PipelineBoard) groups leads by our own categories. Deals
// imported from HubSpot live in HubSpot's stages, which can be any number and
// belong to one of several pipelines; each deal keeps its stage id, label and
// position (`hubspotStage*`), so this board shows them exactly where HubSpot
// does. Read-only: with HubSpot as the source of truth («Solo importar») a
// stage is changed there and arrives with the next sync.
//
// Una tarjeta se abre en su ficha (DealDetail, #1316), donde está la demo del
// negocio. La tarjeta lleva un distintivo «Demo» cuando el negocio ya tiene su
// cuenta de demostración.
//
// Same look as the lead board (header, KPI strip, coloured columns) and the same
// layout contract: it fills the content area and scrolls sideways INSIDE it.
// ===========================================================================

export type StageKind = "open" | "won" | "lost";

export interface BoardColumn {
  id: string;
  label: string;
  kind: StageKind;
  deals: Deal[];
  total: number;
}

export interface BoardPipeline {
  id: string;
  label: string;
  columns: BoardColumn[];
}

/** «Sin producto» in the product filter. */
export const NO_PRODUCT = "__none__";

/** The pipeline a deal belongs to; deals imported before pipelines were stored fall in «default». */
const pipelineOf = (d: Deal) => d.hubspotPipeline || "default";

// Same rule as the backend (hubspotObjects.stageKind): a renamed stage keeps
// the closure HubSpot gave the original, so when the NAME says won or lost,
// the name wins.
const LOST_STAGE_NAME = /perdid|\blost\b|descartad/i;
const WON_STAGE_NAME = /ganad|\bwon\b/i;

/**
 * open | won | lost. By the stage's name when it says so; otherwise by HubSpot's
 * metadata: a closed stage is won at probability 1 and lost at 0.
 */
export function stageKind(stage: { label?: string | null; closed?: boolean; probability?: number | string | null }): StageKind {
  const label = stage.label ?? "";
  if (LOST_STAGE_NAME.test(label)) return "lost";
  if (WON_STAGE_NAME.test(label)) return "won";
  if (!stage.closed) return "open";
  return Number(stage.probability) >= 1 ? "won" : "lost";
}

/**
 * Deals grouped the way HubSpot shows them. Columns come from the portal's
 * pipelines when we have them (so empty stages show too); a stage only known
 * from the deals themselves is appended using the label and position each deal
 * carries. Deals with no HubSpot stage are left out.
 */
export function buildDealsBoard(deals: Deal[], pipelines: HubspotPipeline[]): BoardPipeline[] {
  const staged = deals.filter((d) => d.hubspotStage);
  const out = new Map<string, BoardPipeline>();
  const columnOf = new Map<string, BoardColumn>();
  const key = (pipelineId: string, stageId: string) => `${pipelineId}::${stageId}`;

  for (const p of pipelines) {
    const board: BoardPipeline = { id: p.id, label: p.label, columns: [] };
    for (const s of p.stages) {
      const column: BoardColumn = { id: s.id, label: s.label, kind: stageKind(s), deals: [], total: 0 };
      board.columns.push(column);
      columnOf.set(key(p.id, s.id), column);
    }
    out.set(p.id, board);
  }

  // Stages the pipelines call did not bring: ordered by the position stored on the deal.
  const extras = new Map<string, { column: BoardColumn; order: number }[]>();
  for (const d of staged) {
    const pid = pipelineOf(d);
    const k = key(pid, d.hubspotStage!);
    let column = columnOf.get(k);
    if (!column) {
      column = { id: d.hubspotStage!, label: d.hubspotStageLabel || d.hubspotStage!, kind: stageKind({ label: d.hubspotStageLabel }), deals: [], total: 0 };
      columnOf.set(k, column);
      if (!out.has(pid)) out.set(pid, { id: pid, label: d.hubspotPipelineLabel || "Pipeline de HubSpot", columns: [] });
      const list = extras.get(pid) ?? [];
      list.push({ column, order: d.hubspotStageOrder ?? Number.MAX_SAFE_INTEGER });
      extras.set(pid, list);
    }
    column.deals.push(d);
    column.total += d.amount || 0;
  }
  for (const [pid, list] of extras) {
    list.sort((a, b) => a.order - b.order);
    out.get(pid)!.columns.push(...list.map((x) => x.column));
  }

  for (const board of out.values()) {
    for (const column of board.columns) column.deals.sort((a, b) => (b.amount || 0) - (a.amount || 0));
  }
  return [...out.values()];
}

/** Products present on the deals, in a stable order, for the product filter. */
export function productsOf(deals: Deal[]): string[] {
  return [...new Set(deals.map((d) => (d.product ?? "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

/** Product + free-text filter. `product`: "" = all, NO_PRODUCT = deals without one. */
export function filterDeals(deals: Deal[], product: string, query: string): Deal[] {
  const q = query.trim().toLowerCase();
  return deals.filter((d) => {
    const own = (d.product ?? "").trim();
    if (product === NO_PRODUCT ? own !== "" : product !== "" && own !== product) return false;
    if (!q) return true;
    return [d.title, d.company, d.contact].some((v) => (v ?? "").toLowerCase().includes(q));
  });
}

/** Totals of a board for the KPI strip. */
export function boardKpis(board: BoardPipeline | null) {
  const sum = (kind: StageKind) =>
    (board?.columns ?? [])
      .filter((c) => c.kind === kind)
      .reduce((acc, c) => ({ count: acc.count + c.deals.length, total: acc.total + c.total }), { count: 0, total: 0 });
  return { open: sum("open"), won: sum("won"), lost: sum("lost") };
}

// Column accents, same family as the lead board's categories. Open stages walk
// the palette in order; closed ones are always green (won) or red (lost).
const OPEN_DOTS = ["bg-sky-500", "bg-indigo-500", "bg-cyan-500", "bg-violet-500", "bg-amber-500", "bg-orange-500", "bg-pink-500", "bg-teal-500"];
const KIND_DOT: Record<Exclude<StageKind, "open">, string> = { won: "bg-emerald-500", lost: "bg-red-500" };
const KIND_TONE: Record<Exclude<StageKind, "open">, string> = {
  won: "bg-emerald-500/15 text-emerald-600",
  lost: "bg-red-500/15 text-red-500",
};

const PRODUCT_TONE: Record<string, string> = {
  "Coding Cloud": "bg-sky-500/15 text-sky-600",
  Wayak: "bg-indigo-500/15 text-indigo-500",
  Nuvex: "bg-emerald-500/15 text-emerald-600",
  Movik: "bg-orange-500/15 text-orange-600",
};

type TC = ReturnType<typeof buildThemeClasses>;

function Kpi({ tc, label, value, hint, icon, accent }: { tc: TC; label: string; value: string; hint?: string; icon: string; accent?: boolean }) {
  return (
    <div className={`rounded-xl border p-3 ${tc.cardBorder} ${tc.cardBg}`}>
      <div className="mb-1 flex items-center justify-between">
        <span className={`text-xs ${tc.textSecondary}`}>{label}</span>
        <i className={`${icon} ${accent ? "text-primary-500" : tc.textSecondary}`} />
      </div>
      <div className={`text-lg font-bold ${accent ? "text-primary-500" : tc.text}`}>{value}</div>
      {hint && <div className={`text-[11px] ${tc.textSecondary}`}>{hint}</div>}
    </div>
  );
}

export default function DealsBoard() {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const deals = useDeals();
  const [pipelines, setPipelines] = useState<HubspotPipeline[]>([]);
  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [product, setProduct] = useState("");
  const [query, setQuery] = useState("");
  // Se guarda el id y no el negocio: así la ficha siempre pinta el negocio del
  // store (con su puntero de demo al día) y se cierra sola si el negocio se va.
  const [detailId, setDetailId] = useState<string | null>(null);
  const { isReseller: isPlatformAdmin } = useIsReseller();
  const scroller = useRef<HTMLDivElement | null>(null);

  // The live pipelines give the full list of columns. Without them (HubSpot
  // unreachable, or a user who can't see the integration) the board still
  // builds from what each deal carries.
  useEffect(() => {
    let alive = true;
    fetchHubspotPipelines("deals")
      .then((p) => alive && setPipelines(Array.isArray(p) ? p : []))
      .catch(() => alive && setPipelines([]));
    return () => {
      alive = false;
    };
  }, []);

  const products = useMemo(() => productsOf(deals), [deals]);
  const hasUnassigned = useMemo(() => deals.some((d) => d.hubspotStage && !(d.product ?? "").trim()), [deals]);
  const filtered = useMemo(() => filterDeals(deals, product, query), [deals, product, query]);

  const boards = useMemo(() => buildDealsBoard(filtered, pipelines), [filtered, pipelines]);
  // Which pipelines to offer is decided on ALL the deals, so a filter that
  // empties a pipeline does not make it vanish from the selector.
  const allBoards = useMemo(() => buildDealsBoard(deals, pipelines), [deals, pipelines]);
  const offered = allBoards.filter((b) => b.columns.some((c) => c.deals.length));
  const choices = (offered.length ? offered : allBoards).map((b) => boards.find((x) => x.id === b.id) ?? b);
  const board = choices.find((b) => b.id === pipelineId) ?? choices[0] ?? null;
  const kpis = boardKpis(board);

  const detail = detailId ? deals.find((d) => d.id === detailId) ?? null : null;

  const withoutStage = deals.filter((d) => (d.source ?? "").toLowerCase() === "hubspot" && !d.hubspotStage).length;

  const fieldCls = isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900";
  const chip = (active: boolean) =>
    `rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
      active
        ? "border-primary-500 bg-primary-500 text-white"
        : isDarkMode
          ? "border-gray-500 bg-dark-card text-white hover:bg-white/10"
          : "border-gray-300 bg-white text-gray-900 hover:bg-gray-100"
    }`;
  const scrollBy = (direction: 1 | -1) => scroller.current?.scrollBy({ left: direction * 600, behavior: "smooth" });

  let openIndex = 0;

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden p-6">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
            <i className="ri-layout-column-line text-xl" />
          </span>
          <div>
            <h1 className={`text-xl font-bold ${tc.text}`}>Pipeline de negocios</h1>
            <p className={`text-sm ${tc.textSecondary}`}>
              Las mismas etapas que en HubSpot. Un negocio se mueve allá y llega con la siguiente sincronización.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {choices.length > 1 && (
            <select
              aria-label="Pipeline"
              value={board?.id ?? ""}
              onChange={(e) => setPipelineId(e.target.value)}
              className={`rounded-lg border px-3 py-2 text-sm outline-none ${fieldCls}`}
              title="Cambiar de pipeline"
            >
              {choices.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.label}
                </option>
              ))}
            </select>
          )}
          <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}>
            <i className={`ri-search-line ${tc.textSecondary}`} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar negocio, empresa o contacto"
              className={`w-56 bg-transparent text-sm outline-none ${tc.text}`}
            />
          </div>
        </div>
      </div>

      {/* Product filter */}
      <div className="mb-4 flex flex-wrap items-center gap-2" role="group" aria-label="Filtrar por producto">
        <span className={`text-xs ${tc.textSecondary}`}>
          <i className="ri-price-tag-3-line mr-1" />
          Producto
        </span>
        <button className={chip(product === "")} aria-pressed={product === ""} onClick={() => setProduct("")}>
          Todos
        </button>
        {products.map((p) => (
          <button key={p} className={chip(product === p)} aria-pressed={product === p} onClick={() => setProduct(p)}>
            {p}
          </button>
        ))}
        {hasUnassigned && (
          <button className={chip(product === NO_PRODUCT)} aria-pressed={product === NO_PRODUCT} onClick={() => setProduct(NO_PRODUCT)}>
            Sin producto
          </button>
        )}
      </div>

      {/* KPI strip */}
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi tc={tc} label="Negocios abiertos" value={String(kpis.open.count)} icon="ri-folder-open-line" accent />
        <Kpi tc={tc} label="Valor abierto" value={formatMoney(kpis.open.total)} icon="ri-money-dollar-circle-line" />
        <Kpi tc={tc} label="Ganados" value={String(kpis.won.count)} hint={formatMoney(kpis.won.total)} icon="ri-trophy-line" />
        <Kpi tc={tc} label="Perdidos" value={String(kpis.lost.count)} hint={formatMoney(kpis.lost.total)} icon="ri-close-circle-line" />
      </div>

      {withoutStage > 0 && (
        <div className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600">
          {withoutStage} negocio(s) importado(s) todavía no tienen su etapa de HubSpot. Lanza «Importación completa» en
          Configuración → HubSpot para traerla.
        </div>
      )}

      {/* Board */}
      {!board || !board.columns.length ? (
        <div className={`flex-1 p-10 text-center text-sm ${tc.textSecondary}`}>
          <i className="ri-inbox-line mb-2 block text-3xl" />
          Aún no hay negocios con etapa de HubSpot.
        </div>
      ) : (
        <div className="relative flex min-h-0 min-w-0 flex-1">
          <button
            type="button"
            aria-label="Ver etapas anteriores"
            onClick={() => scrollBy(-1)}
            className={`absolute left-1 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border shadow-md ${tc.cardBorder} ${tc.cardBg} ${tc.text} opacity-80 hover:opacity-100`}
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <button
            type="button"
            aria-label="Ver etapas siguientes"
            onClick={() => scrollBy(1)}
            className={`absolute right-1 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border shadow-md ${tc.cardBorder} ${tc.cardBg} ${tc.text} opacity-80 hover:opacity-100`}
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
          <div ref={scroller} data-testid="deals-board" className={`flex min-w-0 flex-1 gap-3 overflow-x-auto pb-2 ${boardScrollbar(isDarkMode)}`}>
            {board.columns.map((column) => {
              const dot = column.kind === "open" ? OPEN_DOTS[openIndex++ % OPEN_DOTS.length] : KIND_DOT[column.kind];
              return (
                <div
                  key={column.id}
                  data-testid={`deals-column-${column.id}`}
                  className={`flex w-72 flex-shrink-0 flex-col rounded-xl border ${tc.cardBorder} ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}
                >
                  <div className={`rounded-t-xl border-b p-3 ${tc.cardBorder} ${tc.cardBg}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${dot}`} />
                        <span className={`truncate text-sm font-semibold ${tc.text}`} title={column.label}>
                          {column.label}
                        </span>
                        <span className={`rounded-full px-1.5 text-xs ${isDarkMode ? "bg-dark-card text-gray-400" : "bg-gray-200 text-gray-600"}`}>
                          {column.deals.length}
                        </span>
                      </div>
                      {column.kind !== "open" && (
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${KIND_TONE[column.kind]}`}>
                          {column.kind === "won" ? "Ganado" : "Perdido"}
                        </span>
                      )}
                    </div>
                    <div className={`mt-1 text-xs ${tc.textSecondary}`}>{formatMoney(column.total)}</div>
                  </div>

                  <div className={`flex flex-1 flex-col gap-2 overflow-y-auto p-2 ${boardScrollbar(isDarkMode)}`}>
                    {column.deals.length === 0 ? (
                      <div className={`rounded-lg border border-dashed py-6 text-center text-xs ${tc.cardBorder} ${tc.textSecondary}`}>
                        Sin negocios
                      </div>
                    ) : (
                      column.deals.map((d) => {
                        // Distintivo de la demo: su estado o, si la hay, la acción programada.
                        const demoBadge = demoBadgeOf(d.demo);
                        return (
                        <button
                          type="button"
                          key={d.id}
                          data-testid={`deal-card-${d.id}`}
                          onClick={() => setDetailId(d.id)}
                          title="Ver el negocio"
                          className={`w-full cursor-pointer rounded-lg border p-3 text-left shadow-sm transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${tc.cardBorder} ${isDarkMode ? "bg-dark-card" : "bg-white"}`}
                        >
                          <div className="mb-1 flex items-start gap-2">
                            <span className={`mt-1 h-2 w-2 flex-shrink-0 rounded-full ${dot}`} />
                            <p className={`min-w-0 flex-1 text-sm font-semibold leading-tight ${tc.text}`}>{d.title}</p>
                            {demoBadge && (
                              <span
                                data-testid={`deal-demo-badge-${d.id}`}
                                title={`Demo: ${demoBadge.label}`}
                                className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${demoBadge.tone}`}
                              >
                                <i className={`${d.demo?.pending ? "ri-timer-line" : "ri-flask-line"} mr-0.5`} />
                                {d.demo?.pending ? demoBadge.label : "Demo"}
                              </span>
                            )}
                          </div>
                          <p className="mb-1 text-sm font-bold text-primary-500">{formatMoney(d.amount)}</p>
                          {d.company && (
                            <p className={`truncate text-xs ${tc.textSecondary}`}>
                              <i className="ri-building-line mr-1" />
                              {d.company}
                            </p>
                          )}
                          {d.contact && (
                            <p className={`truncate text-xs ${tc.textSecondary}`}>
                              <i className="ri-user-line mr-1" />
                              {d.contact}
                            </p>
                          )}
                          <div className="mt-2 flex items-center justify-between">
                            {d.product ? (
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${PRODUCT_TONE[d.product] ?? "bg-gray-400/15 text-gray-500"}`}>
                                {d.product}
                              </span>
                            ) : (
                              <span />
                            )}
                            {d.closeDate && (
                              <span className={`text-[11px] ${tc.textSecondary}`}>
                                <i className="ri-calendar-line mr-1" />
                                {String(d.closeDate).slice(0, 10)}
                              </span>
                            )}
                          </div>
                        </button>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Ficha del negocio */}
      {detail && (
        <Modal
          title={`Negocio — ${detail.title}`}
          onClose={() => setDetailId(null)}
          themeClasses={tc}
          content={
            <DealDetail
              deal={detail}
              tc={tc}
              isDarkMode={isDarkMode}
              isAdmin={isPlatformAdmin}
              onDemoChanged={(demo) => setDealDemo(detail.id, demo)}
            />
          }
        />
      )}
    </div>
  );
}
